/**
 * Open an external URL.
 *
 * WEB: `window.open(url, '_blank', 'noopener,noreferrer')`. Note the hardening
 * flags: call sites that need the returned handle (an OAuth popup posting back
 * via `window.opener`) must NOT use this helper — see MetaLeadsConnect.
 * NATIVE (Capacitor): `@capacitor/browser` opens an in-app system browser
 * (SFSafariViewController / Chrome Custom Tab). For app deep-links like
 * `https://wa.me/...` the in-app tab redirects out to WhatsApp as expected, and
 * the user returns to the app afterwards instead of being kicked into Safari.
 */
import { isNative } from './platform';

export async function openExternal(url) {
  if (!url) return;
  if (isNative) {
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url });
      return;
    } catch {
      // fall through to the web behavior if the plugin is unavailable
    }
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Props to spread onto an existing `<a target="_blank">` so it opens in the
 * in-app browser on native instead of ejecting the user into Safari/Chrome
 * with no way back.
 *
 * Returns `{}` on web — spreading an empty object adds nothing, so the anchor
 * keeps its exact previous markup and behaviour in a browser.
 *
 *   <a href={url} target="_blank" rel="noreferrer" {...externalLinkProps(url)}>
 *
 * If the anchor already has an onClick, don't spread this — call
 * `openExternal()` from inside that handler instead.
 */
export function externalLinkProps(url) {
  if (!isNative) return {};
  return {
    onClick: (e) => {
      e.preventDefault();
      e.stopPropagation();
      openExternal(url);
    },
  };
}

export default openExternal;
